import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Tooltip,
  Alert,
  Chip,
} from '@mui/material';
import { BiSolidCheckCircle, BiSolidError, BiIdCard, BiShieldX, BiLogOut, BiUserPlus } from 'react-icons/bi';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { type GetUserDetailsQuery } from '../queries/getUserDetails.query.generated';
import { useGetOrgUsersForSuperAdminCheckQuery } from '../queries/getOrgUsersForSuperAdminCheck.query.generated';
import { useUser } from '@store';
import UserStatusDialog from './UserStatusDialog';
import {
  isSuperAdmin,
  canDemoteSuperAdmin,
  canDeactivateUser,
  calculateTargetRole,
  ROLE_DEALER,
  ROLE_DEALER_ADMIN,
  ROLE_DEALER_SUPER_ADMIN,
  ROLE_DRIVER,
  DEALER_ROLES,
} from '../utils/roleUtils';
import { useRoleManagement } from '../hooks/useRoleManagement';
import { useFeaturePermission } from '@utils/featurePermissions';
import { isLiteTier } from '@utils/tiers';
import { FeatureName } from '@utils/featureNames';
import { authService } from '@features/auth/services/authService';
import { ProfileLayout } from '@components/ProfileLayout';
import type { ProfileSectionType } from '@components/ProfileLayout';
import RoleChip from './RoleChip';

type UserDetail = NonNullable<GetUserDetailsQuery['users']>[0];

interface UserProfileTabProps {
  user: UserDetail;
  refetch: () => void;
}

const UserProfileTab: React.FC<UserProfileTabProps> = ({ user, refetch }) => {
  const currentUser = useUser();
  const hasUserManagement = useFeaturePermission(FeatureName.USER_MANAGEMENT);

  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [demoteToDriverOpen, setDemoteToDriverOpen] = useState(false);
  const [promoting, setPromoting] = useState(false);

  // Role management (promote/demote, deactivate/reactivate, sync)
  const {
    assignRole,
    deactivateUser,
    reactivateUser,
    syncPermissions,
    loading,
  } = useRoleManagement();

  // Role detection
  const isDealerRole = user.default_role.includes('dealer');
  const isDriverOnly = user.default_role === ROLE_DRIVER;
  // Cross-rooftop user-tier read (a named exception that stays on `customers.tier`): the
  // product-access snapshot covers only the working rooftop, so there is no resolved model for
  // the user's other linked rooftops.
  const hasLiteRooftop = user.users_usertocustomers.some((utc: any) => isLiteTier(utc.customer?.tier));
  const canPromoteToDealer = isDriverOnly && hasLiteRooftop && hasUserManagement;
  const orgId = user.customer?.organization?.id ??
    user.users_usertocustomers.find(utc => utc.customer.organization?.id)?.customer.organization?.id ?? null;

  // Role promotion cap: current user can only promote up to their own role level
  const currentUserRole = currentUser?.profile?.default_role ?? '';
  const canPromoteToSuperAdmin = currentUserRole === ROLE_DEALER_SUPER_ADMIN;

  const orgUsersWhere = useMemo(() => {
    if (!orgId) return null;
    return {
      active: { _eq: true },
      default_role: { _in: [...DEALER_ROLES] },
      _or: [
        { customer: { organization_id: { _eq: orgId } } },
        { users_usertocustomers: { customer: { organization_id: { _eq: orgId } } } },
      ],
    };
  }, [orgId]);

  const { data: orgUsersData, refetch: refetchOrgUsers } = useGetOrgUsersForSuperAdminCheckQuery({
    variables: { where: orgUsersWhere! },
    skip: !orgUsersWhere || !isDealerRole,
  });

  const orgUsers = orgUsersData?.users ?? [];
  const userIsSuperAdmin = isSuperAdmin(user.default_role);
  const canDemote = userIsSuperAdmin && canDemoteSuperAdmin(orgUsers, user.id);
  const canDeactivate = canDeactivateUser(orgUsers, user.id);

  const handleSyncPermissions = async () => {
    try {
      const customerCount = user.users_usertocustomers.length;
      await syncPermissions(user.id, user.default_role, customerCount);
      toast.success('Permissions synced');
      refetch();
    } catch (err: any) {
      toast.error(`Failed to sync permissions: ${err.message}`);
    }
  };

  const handleDeactivate = async () => {
    try {
      await deactivateUser(user.id);
      toast.success('User deactivated');
      setDeactivateOpen(false);
      refetch();
    } catch (err: any) {
      toast.error(`Failed to deactivate user: ${err.message}`);
    }
  };

  const handleReactivate = async () => {
    try {
      await reactivateUser(user.id);
      toast.success('User reactivated');
      setReactivateOpen(false);
      refetch();
    } catch (err: any) {
      toast.error(`Failed to reactivate user: ${err.message}`);
    }
  };

  const handlePromoteToSuperAdmin = async () => {
    try {
      await assignRole(user.id, ROLE_DEALER_SUPER_ADMIN);
      toast.success('User promoted to Super Admin');
      refetchOrgUsers();
      refetch();
    } catch (err: any) {
      toast.error(`Failed to promote user: ${err.message}`);
    }
  };

  const handleDemoteFromSuperAdmin = async () => {
    try {
      const rooftopCount = user.users_usertocustomers?.length ?? 0;
      const targetRole = rooftopCount >= 2 ? ROLE_DEALER_ADMIN : ROLE_DEALER;
      await assignRole(user.id, targetRole);
      toast.success('Super Admin access removed');
      refetchOrgUsers();
      refetch();
    } catch (err: any) {
      toast.error(`Failed to demote user: ${err.message}`);
    }
  };

  const handleDemoteToDriver = async () => {
    try {
      await assignRole(user.id, ROLE_DRIVER);
      toast.success('User demoted to driver only');
      setDemoteToDriverOpen(false);
      refetch();
    } catch (err: any) {
      toast.error(`Failed to demote user: ${err.message}`);
    }
  };

  const handlePromoteToPortalUser = async () => {
    setPromoting(true);
    try {
      const customerCount = user.users_usertocustomers.length;
      const targetRole = calculateTargetRole(ROLE_DEALER, customerCount) ?? ROLE_DEALER;
      await assignRole(user.id, targetRole);
      toast.success(`Promoted to ${targetRole === ROLE_DEALER_ADMIN ? 'Dealer Admin' : 'Dealer'}`);
      refetch();
    } catch (err: any) {
      toast.error(`Failed to promote user: ${err.message}`);
    } finally {
      setPromoting(false);
    }
  };

  // ── Section definitions ──

  const accountFields = [
    {
      label: 'User ID',
      value: user.id,
      mono: true,
    },
    {
      label: 'Role',
      value: <RoleChip role={user.default_role} />,
    },
    {
      label: 'Created',
      value: dayjs(user.created_at).format('MMM D, YYYY h:mm A'),
    },
    {
      label: 'Updated',
      value: dayjs(user.updated_at).format('MMM D, YYYY h:mm A'),
    },
  ];

  // Build super admin section extra content
  let superAdminExtra: React.ReactNode = null;
  if (isDealerRole) {
    if (!userIsSuperAdmin && canPromoteToSuperAdmin) {
      superAdminExtra = (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Super Admins can manage all users across their organization, including
            adding/removing customer access and modifying feature permissions.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={handlePromoteToSuperAdmin}
            disabled={loading.update || loading.refresh}
          >
            {loading.update ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
            Promote to Super Admin
          </Button>
        </Box>
      );
    } else if (!userIsSuperAdmin && !canPromoteToSuperAdmin) {
      superAdminExtra = (
        <Typography variant="body2" color="text.secondary">
          Only Super Admins can promote users to the Super Admin role.
        </Typography>
      );
    } else if (userIsSuperAdmin && canDemote) {
      superAdminExtra = (
        <Box>
          <Chip
            icon={<BiSolidCheckCircle />}
            label="This user is a Super Admin"
            color="success"
            variant="outlined"
            sx={{ mb: 2 }}
          />
          {canPromoteToSuperAdmin && (
            <Box>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={handleDemoteFromSuperAdmin}
                disabled={loading.update || loading.refresh}
              >
                {loading.update ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                Remove Super Admin
              </Button>
            </Box>
          )}
        </Box>
      );
    } else if (userIsSuperAdmin && !canDemote) {
      superAdminExtra = (
        <Box>
          <Chip
            icon={<BiSolidError />}
            label="This user is the only Super Admin"
            color="warning"
            variant="outlined"
            sx={{ mb: 2 }}
          />
          <Alert severity="warning" sx={{ mt: 1 }}>
            Cannot remove &mdash; this organization needs at least one Super Admin.
          </Alert>
        </Box>
      );
    }
  }

  // Build portal access section — promote (driver→dealer) or demote (dealer→driver)
  let portalAccessExtra: React.ReactNode = null;
  const showPortalAccess = canPromoteToDealer || isDealerRole;

  if (canPromoteToDealer) {
    portalAccessExtra = (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Promoting this user will grant them access to the dealer portal in addition to the mobile app.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={handlePromoteToPortalUser}
          disabled={promoting || loading.update || loading.refresh}
        >
          {promoting ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
          Promote to Portal User
        </Button>
      </Box>
    );
  } else if (isDealerRole) {
    if (userIsSuperAdmin) {
      portalAccessExtra = (
        <Alert severity="info">
          Remove Super Admin role before demoting this user to driver.
        </Alert>
      );
    } else {
      portalAccessExtra = (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Demoting this user to driver will remove their portal access. They will only be able to use the mobile app.
          </Typography>
          <Button
            variant="outlined"
            color="warning"
            size="small"
            onClick={() => setDemoteToDriverOpen(true)}
            disabled={loading.update || loading.refresh}
          >
            Demote to Driver Only
          </Button>
        </Box>
      );
    }
  }

  const sections: ProfileSectionType[] = [
    {
      title: 'Account',
      icon: BiIdCard,
      fields: accountFields,
      extra: (
        <Button
          variant="outlined"
          size="small"
          onClick={handleSyncPermissions}
          disabled={loading.refresh}
        >
          {loading.refresh ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
          Sync Permissions
        </Button>
      ),
    },
    {
      title: 'Admin Access',
      icon: BiShieldX,
      fields: [],
      hidden: !isDealerRole || !hasUserManagement,
      extra: superAdminExtra,
    },
    {
      title: 'Portal Access',
      icon: canPromoteToDealer ? BiUserPlus : BiLogOut,
      fields: [],
      hidden: !showPortalAccess || !hasUserManagement,
      extra: portalAccessExtra,
    },
  ];

  // Danger zone
  const handleResetPassword = async () => {
    if (!user.email) return;
    try {
      await authService.resetPassword(user.email);
      toast.success(`Password reset email sent to ${user.email}`);
    } catch (err: any) {
      toast.error(`Failed to send password reset: ${err.message}`);
    }
  };

  const dangerActions = (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      <Button variant="outlined" size="small" onClick={handleResetPassword}>
        Reset Password
      </Button>
      {user.active ? (
        <Tooltip title={!canDeactivate ? 'Cannot deactivate the only Super Admin for this organization' : ''}>
          <span>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => setDeactivateOpen(true)}
              disabled={!canDeactivate}
            >
              Deactivate User
            </Button>
          </span>
        </Tooltip>
      ) : (
        <Button
          variant="outlined"
          color="success"
          size="small"
          onClick={() => setReactivateOpen(true)}
        >
          Reactivate User
        </Button>
      )}
    </Box>
  );

  const dangerDescription = user.active
    ? 'Deactivating this user will immediately revoke their access to all HopDrive systems. You can reactivate them later if needed.'
    : 'This user is currently deactivated. Reactivating will restore their access with their previous permissions.';

  return (
    <Box>
      <ProfileLayout
        sections={sections}
        editing={false}
        onEdit={() => {}}
        editableSections={[]}
        dangerZone={{
          title: 'Dangerous Operations',
          description: dangerDescription,
          actions: dangerActions,
        }}
      />

      {/* Dialogs */}
      <UserStatusDialog
        variant="deactivate"
        open={deactivateOpen}
        onClose={() => setDeactivateOpen(false)}
        onConfirm={handleDeactivate}
        userName={user.display_name}
        loading={loading.deactivate}
      />
      <UserStatusDialog
        variant="reactivate"
        open={reactivateOpen}
        onClose={() => setReactivateOpen(false)}
        onConfirm={handleReactivate}
        userName={user.display_name}
        loading={loading.reactivate}
      />
      <UserStatusDialog
        variant="demote"
        open={demoteToDriverOpen}
        onClose={() => setDemoteToDriverOpen(false)}
        onConfirm={handleDemoteToDriver}
        userName={user.display_name}
        loading={loading.update}
      />
    </Box>
  );
};

export default UserProfileTab;
